import { AppHeader, Stack, Paper, Box, Typography, Grid, Button, Icon } from '@ace-clublabs/shared-component-library';
import { FlowType } from '@ace-clublabs/common-membership-api-types';
import { useEffect, useLayoutEffect, useState } from 'react';
import LogCannon from '@ace-clublabs/log-cannon';
import { NewJoinConfirmationData, Pages } from '@lib/types';
import { IJoinConfirmationRequest } from '../api/contracts';
import { sources } from '../api/sources';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { DownloadAppSection } from '../components/ConfirmationPageComponents/DownloadAppSection';
import { StatelessRegisterAccount } from '../components/ConfirmationPageComponents/StatelessComponents/StatelessRegisterAccount';
import { StatelessConfirmationSummary } from '../components/ConfirmationPageComponents/StatelessComponents/StatelessConfirmationSummary';
import { StatelessPaymentDetails } from '../components/ConfirmationPageComponents/StatelessComponents/StatelessPaymentDetails';
import { DisclaimerComponent } from '../components/ConfirmationPageComponents/DisclaimerComponent';
import { getLogInPage } from '../utils/helpers';
import { NewJoinScrollView } from '../components/ScrollView';
import { useHistory } from 'react-router-dom';
import { UtagHelper, UtagType } from '../utils/UtagHelper';

const handleLoginClick = () => {
  const url = getLogInPage(window.location.hostname);
  window.open(url);
};

const handleRoadsideClick = () => {
  const newTabbedWindow = window.open('', '_blank');
  (newTabbedWindow as Window).location = 'https://chat.ace.aaa.com/';
};

export const StatelessConfirmationPage = () => {
  const history = useHistory();
  const [confirmation, setConfirmation] = useState<NewJoinConfirmationData | undefined>(undefined);

  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get('sessionId');
  const defaultFlow =
    searchParams.get('flow') ?? (document?.referrer?.includes('/membership/rejoin') ? FlowType.REJOIN : undefined);
  const [flow, setFlow] = useState<FlowType | undefined>(defaultFlow as FlowType);
  const clubCode = confirmation?.clubCode || '004';
  const { email, autoPayStatus } = confirmation || {};

  const isRejoinFlow = flow === FlowType.REJOIN;

  const [isLoading, setIsLoading] = useState(true);
  const [completeRegistration, setCompleteRegistration] = useState(false);

  const page = `membership:${isRejoinFlow ? 'rejoin' : 'join'}:confirmation`;

  const baseConfirmationTags = {
    ...UtagHelper.buildStatelessBaseTags(confirmation?.tags || {}),
    page,
    sessionid: UtagHelper.getAnalyticsSessionId(),
  };

  const sendTag = (type: UtagType, tags: Record<string, string> | undefined) => {
    switch (type) {
      case UtagType.view:
        UtagHelper.dispatchTntSpaEvent(tags!);
        UtagHelper.view(tags);
        break;
      default:
        UtagHelper.link(tags);
        break;
    }
  };

  const getConfirmationInfo = async () => {
    try {
      const request: IJoinConfirmationRequest = { sessionId: sessionId || '' };
      const confirmation = await sources.getConfirmation(request);
      if (!confirmation || !confirmation?.membershipNumber) {
        LogCannon.error('Confirmation info not found', { sessionId, response: confirmation });
        history.push({
          pathname: Pages.SorryPage,
          state: { referer: Pages.StatelessConfirmationPage, flow },
        });
        return;
      }
      setConfirmation(confirmation);
      setFlow(confirmation.appName as FlowType);
      setIsLoading(false);
    } catch (e) {
      LogCannon.error('Error fetching confirmation info', e as any);
      history.push({
        pathname: Pages.SorryPage,
        state: { referer: Pages.StatelessConfirmationPage, flow },
      });
    }
  };

  useEffect(() => {
    if (sessionId) getConfirmationInfo();
  }, [sessionId]);

  useEffect(() => {
    if (isRejoinFlow) {
      setCompleteRegistration(true);
    }
  }, [flow]);

  useLayoutEffect(() => {
    if (!confirmation?.tags) return;

    if (!isRejoinFlow && completeRegistration) {
      sendTag(UtagType.link, {
        ...baseConfirmationTags,
        cta: 'membership:join:membership registration complete',
        page: `${page}-membership registration success`,
      });
    } else {
      sendTag(UtagType.view, {
        ...confirmation?.tags,
        page,
        sessionid: UtagHelper.getAnalyticsSessionId(),
      });
    }
  }, [confirmation, completeRegistration]);

  return (
    <>
      <AppHeader sx={{ padding: 0 }} />
      {isLoading ? (
        <LoadingIndicator maxSteps={3} currentStep={1} loadingInfo={{ isLoading: true, loadingDetails: [] }} />
      ) : (
        <Box className="stateless-confirmation-page-container" marginBottom={'2.375rem'}>
          <Typography variant="h5" className="confirmation-title" sx={{ marginBottom: '0.5rem' }}>
            {isRejoinFlow
              ? `Welcome back, ${confirmation?.firstName}! Enjoy your exclusive member benefits today`
              : !completeRegistration
                ? `Welcome to AAA, ${confirmation?.firstName}!`
                : 'Start enjoying your exclusive AAA benefits'}
          </Typography>
          <Typography variant="b1Regular" className="confirmation-description">
            You will be receiving additional membership information by email.
          </Typography>
          <Box hidden={!completeRegistration} marginTop={'1.5rem'}>
            <Stack
              direction={'row'}
              flexWrap="wrap"
              className="roadside-login-cta-stack"
              gap="0.5rem"
              marginBottom="1rem"
            >
              <Button
                label="Roadside"
                onClick={() => {
                  sendTag(UtagType.link, {
                    ...baseConfirmationTags,
                    cta: `${page}${!isRejoinFlow ? '-membership registration success' : ''}-roadside assistance chatbot click`,
                    page: `${page}${!isRejoinFlow ? '-membership registration success' : ''}`,
                  });
                  handleRoadsideClick();
                }}
                variant="contained"
                size="small"
                id="roadsideButton"
                startIcon={<Icon iconName="truck-duotone" />}
                rounded
                sx={{
                  background: '#DC4731',
                  border: 'none',
                  ':hover': { background: '#B3321E' },
                }}
              ></Button>
              <Button
                label="Log in"
                onClick={handleLoginClick}
                variant="outlined"
                size="small"
                id="loginButton"
                startIcon={<Icon iconName="user-duotone" />}
                rounded
                sx={{
                  marginTop: '0rem !important',
                  marginBottom: '0rem !important',
                }}
              ></Button>
            </Stack>
            <Stack className="scroll-view-section" marginTop="1rem">
              <NewJoinScrollView />
            </Stack>
          </Box>
          {!completeRegistration && (
            <StatelessRegisterAccount
              defaultEmail={email}
              sessionId={sessionId!}
              onRegistrationComplete={() => setCompleteRegistration(true)}
              tags={baseConfirmationTags}
            />
          )}
          {completeRegistration && (
            <Box marginTop="1.25rem">
              <DownloadAppSection />
            </Box>
          )}
          <Grid
            container
            columnSpacing={'1rem'}
            rowSpacing={'1rem'}
            columns={12}
            sx={{ margin: '1.5rem 0', height: 'fit-content' }}
          >
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Paper
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '0.5rem',
                  boxShadow: '0 -2px 1px 0 rgba(8, 17, 34, 0.04), 0 2px 4px 0 rgba(8, 17, 34, 0.20)',
                }}
              >
                <StatelessConfirmationSummary {...confirmation!} />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Paper
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '0.5rem',
                  boxShadow: '0 -2px 1px 0 rgba(8, 17, 34, 0.04), 0 2px 4px 0 rgba(8, 17, 34, 0.20)',
                }}
              >
                <StatelessPaymentDetails {...confirmation!} />
              </Paper>
            </Grid>
          </Grid>

          {!completeRegistration && <DownloadAppSection />}
          <Stack className="membership-disclaimer-section" direction="column" gap={'1rem'} margin={'1rem 0'}>
            {/* B2B hardcoding ERS fee */}
            <DisclaimerComponent clubCode={clubCode} isERSSelected={false} ersFee={125} />
            {autoPayStatus === 'succeeded' && (
              <Typography variant="b3Regular" sx={{ color: '#404040' }}>
                You have elected to enroll into automatic payment. You will be charged automatically when your
                membership renews.
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </>
  );
};
